import { Add as AddIcon } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  TextField,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import LocalFilePicker from "../../../components/associatedFiles/LocalFilePicker";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";

const AddMode = {
  LINK: "LINK",
  DISK: "DISK",
  CLIPBOARD: "CLIPBOARD",
};

interface Props {
  readonly defaultFileName?: string;
  readonly readonly?: boolean;
  readonly onAdd: (formData: FormData) => Promise<void>;
}

export const AssociatedFilesDialog: FC<Props> = ({ defaultFileName = "", readonly, onAdd }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState(AddMode.LINK);
  const [filename, setFilename] = useState(defaultFileName);
  const [fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [hiddenFile, setHiddenFile] = useState(true);
  const [clipboardImage, setClipboardImage] = useState<File | undefined>(undefined);
  const [clipboardPreviewUrl, setClipboardPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    setFilename(defaultFileName);
    setFileUrl("");
    setFile(undefined);
    setHiddenFile(true);
    setClipboardImage(undefined);
    setClipboardPreviewUrl(undefined);
  }, [dialogOpen, mode]);

  useEffect(() => {
    return () => {
      if (clipboardPreviewUrl) {
        URL.revokeObjectURL(clipboardPreviewUrl);
      }
    };
  }, [clipboardPreviewUrl]);

  const handlePasteFromClipboard = async () => {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const imageType = item.types.find((type) => type.startsWith("image/"));
      if (imageType) {
        const blob = await item.getType(imageType);
        const extension = imageType.split("/")[1];
        const pastedFile = new File([blob], `clipboard.${extension}`, { type: imageType });
        const url = URL.createObjectURL(pastedFile);
        setClipboardImage(pastedFile);
        setClipboardPreviewUrl(url);
        break;
      }
    }
  };

  const handleAdd = async () => {
    const formData = new FormData();
    formData.append("name", filename);
    formData.append("attachmentType", hiddenFile ? "PRIVATE" : "PUBLIC");
    if (mode === AddMode.LINK) {
      formData.append("url", fileUrl);
    } else if (mode === AddMode.DISK) {
      formData.append("file", file!, file!.name);
    } else if (mode === AddMode.CLIPBOARD) {
      formData.append("file", clipboardImage!, clipboardImage!.name);
    }
    await onAdd(formData);
    handleClose();
  };

  const handleClose = () => setDialogOpen(false);

  const addEnabled =
    filename.length > 0 &&
    ((mode === AddMode.LINK && fileUrl?.length > 0) ||
      (mode === AddMode.DISK && !!file) ||
      (mode === AddMode.CLIPBOARD && !!clipboardImage));
  return (
    <>
      <Dialog open={dialogOpen} onClose={() => handleClose()}>
        <DialogTitle>Załącz plik</DialogTitle>
        <DialogContent>
          <TabContext value={mode}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={(_, newValue) => setMode(newValue)}>
                <Tab label="Dodaj link" value={AddMode.LINK} />
                <Tab label="Wybierz z dysku" value={AddMode.DISK} />
                <Tab label="Ze schowka" value={AddMode.CLIPBOARD} />
              </TabList>
            </Box>
            <TabPanel value={AddMode.LINK}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                <TextField
                  label="Adres URL"
                  type="url"
                  fullWidth
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                />
              </Stack>
            </TabPanel>
            <TabPanel value={AddMode.DISK}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                <LocalFilePicker onFileChange={setFile} />
              </Stack>
            </TabPanel>
            <TabPanel value={AddMode.CLIPBOARD}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                <Button variant="outlined" onClick={handlePasteFromClipboard}>
                  Wklej ze schowka
                </Button>
                {clipboardPreviewUrl && (
                  <Box
                    component="img"
                    src={clipboardPreviewUrl}
                    alt="Podgląd"
                    sx={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                  />
                )}
              </Stack>
            </TabPanel>
          </TabContext>
          <FormControlLabel
            control={<Checkbox checked={hiddenFile} onChange={(e) => setHiddenFile(e.target.checked)} />}
            label="Niewidoczny dla klientów"
          />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => handleClose()}>
            Anuluj
          </Button>
          <AsyncLoadingButton disabled={!addEnabled} onClick={handleAdd}>
            Dodaj
          </AsyncLoadingButton>
        </DialogActions>
      </Dialog>
      <Tooltip title="Dodaj załącznik">
        <span>
          <IconButton disabled={readonly} onClick={() => setDialogOpen(true)} aria-label="Dodaj załącznik">
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};
